import {
  ActionIcon,
  Badge,
  Button,
  Drawer,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconChevronDown,
  IconChevronRight,
  IconEdit,
  IconPlus,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, getApiErrorMessage } from "../../lib/api";
import type { CategoryTreeNode } from "../../types/category";
import { formatPriceRange } from "../../utils/price";

// --- Types ---

interface FlatCategory extends Omit<CategoryTreeNode, "children"> {
  _hasChildren: boolean;
  _parentId: string | null;
}

// --- Helpers ---

function flattenTree(
  nodes: CategoryTreeNode[],
  parentId: string | null = null,
): FlatCategory[] {
  const result: FlatCategory[] = [];
  for (const node of nodes) {
    const { children, ...rest } = node;
    result.push({ ...rest, _hasChildren: children.length > 0, _parentId: parentId });
    if (children.length > 0) {
      result.push(...flattenTree(children, node.id));
    }
  }
  return result;
}

// --- Component ---

export function CategoriesPage() {
  const navigate = useNavigate();
  const [tree, setTree] = useState<CategoryTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [editingCategory, setEditingCategory] = useState<FlatCategory | null>(null);
  const [createDepth, setCreateDepth] = useState(0);

  // Form state
  const [formName, setFormName] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [formParentId, setFormParentId] = useState<string | null>(null);
  const [formPriceFloor, setFormPriceFloor] = useState<number | string>("");
  const [formPriceCeiling, setFormPriceCeiling] = useState<number | string>("");
  const [formAttrSchema, setFormAttrSchema] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchTree = useCallback(async () => {
    try {
      const res = await api.get<CategoryTreeNode[]>("/categories/tree");
      setTree(res.data);
    } catch (err) {
      notifications.show({ color: "red", message: getApiErrorMessage(err, "Failed to load categories") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTree();
  }, [fetchTree]);

  const flat = useMemo(() => flattenTree(tree), [tree]);

  // Filter out collapsed descendants
  const visibleRows = useMemo(() => {
    const hidden = new Set<string>();
    for (const cat of flat) {
      if (cat._parentId && (hidden.has(cat._parentId) || collapsed.has(cat._parentId))) {
        hidden.add(cat.id);
      }
    }
    return flat.filter((c) => !hidden.has(c.id));
  }, [flat, collapsed]);

  // Parent options for Select (only categories at the right depth)
  const parentOptions = useMemo(() => {
    return flat
      .filter((c) => c.depth < 3)
      .map((c) => ({
        value: c.id,
        label: `${"  ".repeat(c.depth)}${c.name}`,
      }));
  }, [flat]);

  function toggleCollapse(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleCreate(parentId: string | null, depth: number) {
    setEditingCategory(null);
    setCreateDepth(depth);
    setFormName("");
    setFormSlug("");
    setFormParentId(parentId);
    setFormPriceFloor("");
    setFormPriceCeiling("");
    setFormAttrSchema("");
    openDrawer();
  }

  function handleEdit(cat: FlatCategory) {
    setEditingCategory(cat);
    setCreateDepth(cat.depth);
    setFormName(cat.name);
    setFormSlug(cat.slug);
    setFormParentId(cat._parentId);
    setFormPriceFloor(cat.priceFloor ?? "");
    setFormPriceCeiling(cat.priceCeiling ?? "");
    setFormAttrSchema(
      cat.attributeSchema ? JSON.stringify(cat.attributeSchema, null, 2) : "",
    );
    openDrawer();
  }

  async function handleSave() {
    setSaving(true);
    try {
      // Parse attribute schema if provided
      let attrSchema = null;
      if (formAttrSchema.trim()) {
        try {
          attrSchema = JSON.parse(formAttrSchema);
        } catch (parseErr) {
          const detail = parseErr instanceof SyntaxError ? parseErr.message : "parse error";
          notifications.show({ color: "red", message: `Invalid JSON: ${detail}` });
          setSaving(false);
          return;
        }
      }

      const payload = {
        name: formName,
        slug: formSlug || undefined,
        parent_category_id: formParentId || undefined,
        price_floor: formPriceFloor || undefined,
        price_ceiling: formPriceCeiling || undefined,
        attribute_schema: attrSchema,
      };

      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, payload);
        notifications.show({ color: "green", message: `Updated "${formName}"` });
      } else {
        await api.post("/categories", payload);
        notifications.show({ color: "green", message: `Created "${formName}"` });
      }
      closeDrawer();
      await fetchTree();
    } catch (err) {
      notifications.show({ color: "red", message: getApiErrorMessage(err, "Failed to save category") });
    } finally {
      setSaving(false);
    }
  }

  function handleDelete(cat: FlatCategory) {
    if (cat.productCount > 0 || cat._hasChildren) return;

    modals.openConfirmModal({
      title: `Delete "${cat.name}"?`,
      children: <Text size="sm">This cannot be undone.</Text>,
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await api.delete(`/categories/${cat.id}`);
          notifications.show({ color: "green", message: `Deleted "${cat.name}"` });
          await fetchTree();
        } catch (err) {
          notifications.show({ color: "red", message: getApiErrorMessage(err, "Failed to delete category") });
        }
      },
    });
  }

  const depthStyles: Record<number, { pl: string; fw?: number; bg?: string }> = {
    0: { pl: "sm", fw: 700, bg: "gray.0" },
    1: { pl: "lg" },
    2: { pl: "xl" },
    3: { pl: "48" },
  };

  if (loading) return <Text>Loading categories...</Text>;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Taxonomy</Title>
        <Group gap="sm">
          <Button
            leftSection={<IconSparkles size={16} />}
            variant="light"
            onClick={() => navigate("/admin/classify")}
          >
            Classify Products
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => handleCreate(null, 0)}
          >
            Add L1 Category
          </Button>
        </Group>
      </Group>

      <Table.ScrollContainer minWidth={700}>
        <Table highlightOnHover verticalSpacing="xs" fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Slug</Table.Th>
              <Table.Th ta="center">Products</Table.Th>
              <Table.Th ta="right">Price Range</Table.Th>
              <Table.Th ta="center">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visibleRows.map((cat) => {
              const style = depthStyles[cat.depth] ?? { pl: "sm" };
              const canDelete = cat.productCount === 0 && !cat._hasChildren;

              return (
                <Table.Tr
                  key={cat.id}
                  bg={style.bg}
                >
                  <Table.Td pl={style.pl}>
                    <Group gap={4} wrap="nowrap">
                      {cat._hasChildren ? (
                        <ActionIcon
                          variant="subtle"
                          size="xs"
                          onClick={() => toggleCollapse(cat.id)}
                        >
                          {collapsed.has(cat.id) ? (
                            <IconChevronRight size={14} />
                          ) : (
                            <IconChevronDown size={14} />
                          )}
                        </ActionIcon>
                      ) : (
                        <span style={{ width: 22 }} />
                      )}
                      <Text size="sm" fw={style.fw}>
                        {cat.name}
                      </Text>
                      <Badge variant="light" size="xs" color="gray">
                        L{cat.depth + 1}
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{cat.slug}</Text>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Text size="sm">{cat.productCount}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="xs" c="dimmed">
                      {formatPriceRange(cat.priceFloor, cat.priceCeiling)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Group gap={4} justify="center" wrap="nowrap">
                      {cat.depth < 3 && (
                        <Tooltip label={`Add child`}>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleCreate(cat.id, cat.depth + 1)}
                          >
                            <IconPlus size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Edit">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => handleEdit(cat)}
                        >
                          <IconEdit size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={
                          canDelete
                            ? "Delete"
                            : cat._hasChildren
                              ? "Remove children first"
                              : `${cat.productCount} products assigned`
                        }
                      >
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="red"
                          disabled={!canDelete}
                          onClick={() => handleDelete(cat)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {/* CRUD Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={editingCategory ? `Edit: ${editingCategory.name}` : `Add L${createDepth + 1} Category`}
        position="right"
        size="md"
        padding="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            required
            value={formName}
            onChange={(e) => setFormName(e.currentTarget.value)}
          />
          <TextInput
            label="Slug"
            description="Auto-generated from name if left empty"
            value={formSlug}
            onChange={(e) => setFormSlug(e.currentTarget.value)}
          />
          {(createDepth > 0 || editingCategory) && (
            <Select
              label="Parent Category"
              data={parentOptions}
              value={formParentId}
              onChange={setFormParentId}
              clearable
            />
          )}
          <Group grow>
            <NumberInput
              label="Price Floor"
              prefix="$"
              value={formPriceFloor}
              onChange={setFormPriceFloor}
              min={0}
              decimalScale={2}
            />
            <NumberInput
              label="Price Ceiling"
              prefix="$"
              value={formPriceCeiling}
              onChange={setFormPriceCeiling}
              min={0}
              decimalScale={2}
            />
          </Group>
          <Textarea
            label="Attribute Schema (JSON)"
            description="Define expected attributes for products in this category"
            value={formAttrSchema}
            onChange={(e) => setFormAttrSchema(e.currentTarget.value)}
            minRows={4}
            autosize
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              {editingCategory ? "Update" : "Create"}
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </Stack>
  );
}
